import React, { useContext, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { Button } from "~/ui/components/Button";
import { BigButton } from "~/ui/components/BigButton";
import { keyframes, styled, darkTheme } from "~/ui/style/stitches.config";
import { AuthContext } from "~/utils/useAuth";
import { AuthFormState, AuthPage } from "./login/Auth";
import { createNewQuestion } from "~/api/questionsApi";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";
import { trackEvent } from "~/api/analyticsApi";
import { Gradient } from "~/ui/components/Gradient";
import { isInTrial, isInExtendedTrial } from "~/api/userApi";

export const ParticleBlob: React.FC = () => {
  const mountRef = useRef<HTMLDivElement | null>(null);
  let mouse = new THREE.Vector2();
  let prevMouse = new THREE.Vector2(); // Previous mouse position
  const blobRadius = 2; // Blob size
  const movementThreshold = 0.001; // Minimum mouse movement to trigger interaction
  let time = 0; // Time variable for fluid motion

  useEffect(() => {
    const mount = mountRef.current!;

    // Create scene, camera, and renderer
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true }); // Transparent background
    renderer.setSize(window.innerWidth, window.innerHeight);
    mount.appendChild(renderer.domElement);

    // Particle setup
    const particleCount = 5000;
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const originalPositions = new Float32Array(particleCount * 3); // Store original positions

    for (let i = 0; i < particleCount; i++) {
      const radius = Math.random() * blobRadius;
      const angle = Math.random() * Math.PI * 2;

      const x = radius * Math.cos(angle);
      const y = radius * Math.sin(angle);
      const z = (Math.random() - 0.5) * 0.1; // Keep it flatter for smoother blending

      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;

      originalPositions[i * 3] = x;
      originalPositions[i * 3 + 1] = y;
      originalPositions[i * 3 + 2] = z;
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    // Material with soft blending and smooth color transition
    const material = new THREE.ShaderMaterial({
      uniforms: {
        uCenterColor: { value: new THREE.Color(0xd28fff) }, // Purple center
        uEdgeColor: { value: new THREE.Color(0xffddc1) }, // Peachy edge
        uTime: { value: 0.0 }, // Time for motion
      },
      vertexShader: `
        uniform float uTime;
        varying vec3 vPosition;
        void main() {
          vPosition = position;
          
          // Apply a subtle fluid motion over time
          vec3 fluidMovement = vec3(
            sin(position.x * 2.0 + uTime) * 0.01,
            cos(position.y * 2.0 + uTime) * 0.01,
            sin(position.z * 2.0 + uTime) * 0.01
          );
          
          vec3 newPosition = position + fluidMovement;  // Fluid-like motion
          gl_PointSize = 2.0;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 uCenterColor;
        uniform vec3 uEdgeColor;
        varying vec3 vPosition;

        void main() {
          // Smooth gradient between center and edge colors
          float dist = length(vPosition);
          vec3 color = mix(uCenterColor, uEdgeColor, dist / 1.2);
          gl_FragColor = vec4(color, 0.5);  // Soft transparency

          // Make particles round
          if (length(gl_PointCoord - vec2(0.5)) > 0.4) discard;
        }
      `,
      transparent: true,
      depthTest: false,
      blending: THREE.NormalBlending, // Preserve color accuracy
    });

    const particles = new THREE.Points(geometry, material);
    scene.add(particles);

    // Fix mouse movement issue
    const handleMouseMove = (event: MouseEvent) => {
      mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
      mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
    };

    // Attach the event listener for mouse move
    window.addEventListener("mousemove", handleMouseMove);

    // Animation loop with fluid motion
    const animate = () => {
      requestAnimationFrame(animate);
      time += 0.01;
      material.uniforms.uTime.value = time; // Update time uniform

      const positionsArray = geometry.attributes.position.array as Float32Array;

      for (let i = 0; i < particleCount; i++) {
        const dx = mouse.x * 2 - positionsArray[i * 3];
        const dy = mouse.y * 2 - positionsArray[i * 3 + 1];
        const distance = Math.sqrt(dx * dx + dy * dy);

        // Move particles based on proximity to mouse
        if (distance < 0.5) {
          positionsArray[i * 3] += (positionsArray[i * 3] - mouse.x) * 0.05;
          positionsArray[i * 3 + 1] +=
            (positionsArray[i * 3 + 1] - mouse.y) * 0.05;
        } else {
          // Return particles to original position smoothly
          positionsArray[i * 3] +=
            (originalPositions[i * 3] - positionsArray[i * 3]) * 0.02;
          positionsArray[i * 3 + 1] +=
            (originalPositions[i * 3 + 1] - positionsArray[i * 3 + 1]) * 0.02;
        }
      }

      geometry.attributes.position.needsUpdate = true; // Update positions
      renderer.render(scene, camera); // Render scene
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} />;
};

const themes = {
  work: {
    questions: [
      "What's next in my career?",
      "What wants to come alive in my work?",
      "What should I know about this opportunity?",
      "What is emerging with this launch?",
    ],
    title: "Work stuff. We get it!",
  },
  relationships: {
    questions: [
      "What's the energy between us right now?",
      "How can I show up differently in dating?",
      "What's underneath this current challenge?",
      "What's ready to shift in our relationship?",
    ],
    title: "Ooo. Relationships.",
  },
  "life changes": {
    questions: [
      "What’s next for my work, relationships, and creativity?",
      "Should I stay or should I go?",
      "What wisdom do I need for this decision?",
      "What's holding me back?",
    ],
    title: "Ooo. A plot twist!",
  },
  creativity: {
    questions: [
      "What's blocking my creative flow?",
      "What story is trying to be told?",
      "What's missing from this project?",
      "What's the energy of this collaboration?",
    ],
    title: "Let's figure it out!",
  },
  "inner magic": {
    questions: [
      "What's blocking my intuitive flow?",
      "How does my magic want to play?",
      "What needs to be released?",
      "What curiosities are calling me?",
    ],
    title: "Let's get deep.",
  },
  idk: {
    questions: [
      "What do I need to know about this next month?",
      "What's up with this weird energy?",
      "What bigger picture can I focus on?",
      "Should I live in New York?",
    ],
    title: "Let's explore together",
  },
};

export enum HOMEPAGE_PHASE {
  INITIAL,
  THEME_SELECTION,
  QUESTION_SELECTION,
  SIGN_IN,
}

export const MoonlightHomepage = ({
  isTransitioning,
  setIsTransitioning,
  currPhase,
  setCurrPhase,
}: {
  isTransitioning: boolean;
  setIsTransitioning: Function;
  currPhase: HOMEPAGE_PHASE;
  setCurrPhase: Function;
}) => {
  const [currTheme, setCurrTheme] = useState<keyof typeof themes>(
    Object.keys(themes)[0] as keyof typeof themes
  );
  const [currTitle, setCurrTitle] = useState("");
  const [currQuestion, setCurrQuestion] = useState("");
  const [currSubtitle, setCurrSubtitle] = useState("");
  const [currTrialCopy, setCurrTrialCopy] = useState("");

  const [currContent, setCurrContent] = useState<React.ReactNode>(<div></div>);
  const { user } = useContext(AuthContext);
  const { isConnecting, appDarkMode } = useSnapshot(localState);
  const [currText, setCurrText] = useState("");
  const [isTransitioningToRoom, setIsTransitioningToRoom] = useState(false);

  // Calculate trial copy
  let trialCopy = "";
  if (user && !user.isProfessional && !user.isPlus) {
    const oneDay = 24 * 60 * 60 * 1000;
    const oneHour = 60 * 60 * 1000;
    const today = new Date();
    const trialEnd = new Date(user.trialEndsAt);
    const timeDiff = trialEnd.getTime() - today.getTime();

    const diffHours = Math.floor(timeDiff / oneHour);
    const diffDays = Math.ceil(timeDiff / oneDay);

    // Less than 1 hour
    if (diffHours === 0 && timeDiff > 0) {
      trialCopy = "1 hour left in trial";
    // Less than 9 hours - show hours for urgency
    } else if (diffHours > 0 && diffHours < 9) {
      trialCopy = diffHours === 1 ? "1 hour left in trial" : `${diffHours} hours left in trial`;
    } else if (diffDays === 1) {
      trialCopy = "1 day left in trial";
    } else if (diffDays <= 30 && diffDays > 0) {
      trialCopy = `${diffDays} days left in trial`;
    } else if (diffDays <= 90 && diffDays > 0) {
      const weeks = Math.ceil(diffDays / 7);
      trialCopy = `${weeks} weeks left in trial`;
    } else if (timeDiff <= 0) {
      trialCopy = "trial ended - become a member";
    }
  }

  // Initial content with BigButton (matching iOS HomeRootView)
  const initialContent = (
    <div className="initial-content">
      <BigButton
        cursorClasses={`transitioning-${isTransitioningToRoom}`}
        onClick={() => {
          trackEvent("/home/start_room");
          setCurrPhase(HOMEPAGE_PHASE.THEME_SELECTION);
        }}
        enabled={true}
        text={"CREATE A RITUAL"}
      />
      <div className="taglines">
        <div className="tagline">
          <img src="/images/video.svg" />
          <div className="text">video & audio included</div>
        </div>
      </div>
    </div>
  );

  // Helper function to proceed after question selection
  const proceedAfterQuestion = (question: string, theme: string) => {
    if (user) {
      // Logged in: save question and go directly to room
      createNewQuestion(user.uuid, question, theme);
      document.getElementById("create-room-with-question-button")?.click();
    } else {
      // Not logged in: save question to localStorage for after signup, then go to sign-in
      localStorage.setItem("pendingQuestion", JSON.stringify({ question, theme }));
      setCurrPhase(HOMEPAGE_PHASE.SIGN_IN);
    }
  };

  // Theme selection content - now includes text input first, then topic buttons (matching iOS RoomThemeSelection)
  const themeSelectionContent = (
    <div className="theme-select">
      {/* Custom question input with arrow */}
      <div className="input-row">
        <div className="input-container">
          <input
            id="question-input"
            autoFocus
            maxLength={300}
            className={`selected-${currQuestion === ""}`}
            type={"text"}
            autoComplete="off"
            onFocus={() => {
              setCurrQuestion("");
            }}
            onChange={(e) => {
              setCurrQuestion(e.target.value);
              setCurrText(e.target.value);
            }}
            placeholder="Type your question or scenario..."
          />
          {currText.length >= 100 && (
            <div className="character-counter">{currText.length}/300</div>
          )}
        </div>

        {/* Forward arrow - appears when there's text input */}
        <div className={`forward-arrow-container visible-${currText !== ""}`}>
          <div
            className="forward-arrow-button"
            onClick={() => {
              trackEvent("/home/submit_question", {
                theme: "custom",
                question: currQuestion,
              });
              proceedAfterQuestion(currQuestion, "custom");
            }}
          >
            <img style={{ width: "50%", transform: "rotate(180deg)" }} src="images/back-nav-icon.svg" />
          </div>
        </div>
      </div>

      {/* Topic selection */}
      <div className={`topic-divider has-arrow-${currText !== ""}`}>Or choose a topic.</div>

      <div className="theme-button-container">
        {Object.keys(themes).map((theme) => {
          return (
            <Button
              key={theme}
              onClick={() => {
                trackEvent("/home/choose_theme", {
                  theme,
                });
                setCurrTheme(theme as keyof typeof themes);
                setCurrPhase(HOMEPAGE_PHASE.QUESTION_SELECTION);
              }}
            >
              {theme}
            </Button>
          );
        })}
      </div>

      {/* Skip button */}
      <div
        className="skip-button"
        onClick={() => {
          trackEvent("/home/skip_question");
          setCurrTheme("idk");
          proceedAfterQuestion("", "idk");
        }}
      >
        Skip theme
      </div>

      {/* Back button */}
      <div
        className="back-button"
        onClick={() => {
          setCurrPhase(HOMEPAGE_PHASE.INITIAL);
          setCurrQuestion("");
          setCurrText("");
        }}
      >
        <img style={{ width: "50%" }} src="images/back-nav-icon.svg" />
      </div>
    </div>
  );

  // Question selection content - shows preset questions for selected topic (matching iOS RoomThemeDetailSelection)
  const questionSelectionContent = (
    <div className="question-select">
      {/* Preset questions for the selected theme */}
      <div className="question-option-container visible-true">
        {themes[currTheme].questions.map((question) => (
          <div
            className={`question-option active-${currQuestion === question}`}
            key={question}
            onClick={() => {
              setCurrQuestion(question);
            }}
          >
            {question}
          </div>
        ))}
      </div>

      {/* Forward arrow - appears when a question is selected */}
      <div className={`forward-arrow-container visible-${currQuestion !== ""}`}>
        <div
          className="forward-arrow-button"
          onClick={() => {
            trackEvent("/home/submit_question", {
              theme: currTheme,
              question: currQuestion,
            });
            proceedAfterQuestion(currQuestion, currTheme);
          }}
        >
          <img style={{ width: "50%", transform: "rotate(180deg)" }} src="images/back-nav-icon.svg" />
        </div>
      </div>

      {/* Skip button */}
      <div
        className={`skip-button has-arrow-${currQuestion !== ""}`}
        onClick={() => {
          trackEvent("/home/skip_question");
          proceedAfterQuestion("", currTheme);
        }}
      >
        Skip question
      </div>

      {/* Back button */}
      <div
        className="back-button"
        onClick={() => {
          setCurrQuestion("");
          setCurrPhase(HOMEPAGE_PHASE.THEME_SELECTION);
        }}
      >
        <img style={{ width: "50%" }} src="images/back-nav-icon.svg" />
      </div>
    </div>
  );






  useEffect(() => {
    setIsTransitioning(true);
    setTimeout(() => {
      setIsTransitioning(false);
      document.getElementById("main-container")!.scrollTop = 0;
    }, 800);

    setTimeout(() => {
      if (currPhase === HOMEPAGE_PHASE.INITIAL) {
        setCurrTitle("Hello!");
        setCurrSubtitle("Start your own tarot room.");
        setCurrTrialCopy(trialCopy);
        setCurrContent(initialContent);
      } else if (currPhase === HOMEPAGE_PHASE.THEME_SELECTION) {
        setCurrTitle("Ask a question");
        setCurrSubtitle("");
        setCurrTrialCopy("");
        setCurrContent(themeSelectionContent);
      } else if (currPhase === HOMEPAGE_PHASE.QUESTION_SELECTION) {
        setCurrTitle("Anything in mind?");
        setCurrSubtitle("");
        setCurrTrialCopy("");
        setCurrContent(questionSelectionContent);
      } else if (currPhase === HOMEPAGE_PHASE.SIGN_IN) {
        setCurrTitle("");
        setCurrSubtitle("");
        setCurrTrialCopy("");
        setCurrContent(
          <div style={{
            marginTop: "-10px",
            marginBottom: "150px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}>
            <AuthPage
              state={AuthFormState.REGISTER}
              onlyForms
              redirectDest={"/create-room?useQuestion=true"}
              onSubmitSignIn={() => {
                trackEvent("home/question_sign_in", {
                  theme: currTheme,
                  question: currQuestion,
                });
                // Go directly to room after sign-in
                document.getElementById("create-room-with-question-button")?.click();
              }}
            />
            <div
              className="back-button"
              onClick={() => {
                setCurrPhase(HOMEPAGE_PHASE.THEME_SELECTION);
              }}
            >
              <img style={{ width: "50%" }} src="images/back-nav-icon.svg" />
            </div>
          </div>
        );
      }
    }, 600);
  }, [currPhase]);

  useEffect(() => {
    if (currPhase === HOMEPAGE_PHASE.QUESTION_SELECTION) {
      setCurrContent(questionSelectionContent);
    }
  }, [currQuestion]);

  useEffect(() => {
    if (currPhase === HOMEPAGE_PHASE.THEME_SELECTION) {
      setCurrContent(themeSelectionContent);
    }
  }, [currText]);

  return (
    <StyledMoonlightHomepage
      isConnectingToRoom={isConnecting}
      isTransitioning={isTransitioning}
      style={{
        position: "relative",
        paddingBottom: "200px",
      }}
      onMouseMove={(e) => {
        const cursorDot = document.getElementById("cursor-dot");
        if (cursorDot) {
          cursorDot.style.left = e.clientX + "px";
          cursorDot.style.top = e.clientY + "px";
        }
      }}
    >
      <div className="text-container">
        <div className="title overall-title">{currTitle}</div>
        <div className="subtitle">{currSubtitle}</div>
        <div
          className="trial-copy"
          onClick={() => currTrialCopy && (window.location.href = "/plans")}
          style={{ cursor: currTrialCopy ? "pointer" : "default" }}
        >
          {currTrialCopy}
        </div>
      </div>
      <div className="content"> {currContent}</div>

      <div>
        <Gradient dark={appDarkMode} />
      </div>
    </StyledMoonlightHomepage>
  );
};

const fade1 = keyframes({
  "0%": {
    opacity: 0,
  },
  "10%": { opacity: 0 },
  "60%": { opacity: 1 },
});
const fade2 = keyframes({
  "0%": {
    opacity: 0,
  },
  "35%": { opacity: 0 },
  "85%": { opacity: 1 },
});
const fade3 = keyframes({
  "0%": {
    opacity: 0,
  },
  "60%": { opacity: 0 },
  "100%": { opacity: 1 },
});

const StyledMoonlightHomepage = styled("div", {
  marginTop: "220px",
  width: "550px",

  backgroundColor: "transparent",
  zIndex: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  "& .text-container": {
    marginTop: "-50px",
    transition: "all 0.6s ease-out",
    textAlign: "center",
    position: "relative",
    zIndex: 5,
    [`.${darkTheme.className} &`]: {
      color: "$text",
    },
  },
  "& .trial-copy": {
    fontSize: "10px",
    color: "rgba(0, 0, 0, 0.4)",
    letterSpacing: "0.02em",
    marginTop: "6px",
    marginBottom: "10px",
    [`.${darkTheme.className} &`]: {
      color: "$textMuted",
    },
  },
  "& .reading-question-container": {
    width: "70%",
    textAlign: "center",
    padding: "10px 30px",
    borderRadius: "70px",
    backgroundColor: "rgba(0, 0, 0, 0.04)",
    fontSize: "10px",
    [`.${darkTheme.className} &`]: {
      backgroundColor: "$subtleFill",
      color: "$text",
    },

    textWrap: "wrap",

    transition: "opacity 0.6s ease-in",
    transitionDelay: "0.3s",
    position: "absolute",
    opacity: 0,
    top: "-130px",
    left: "50%",
    transform: "translate(-50%, 0)",
    "&.active-true": {
      opacity: 1,
    },
    "@mobile": {
      width: "calc(100vw - 140px)",
    },
  },

  "& .overall-title": {
    marginBottom: "20px",
    fontSize: "46px !important",
    "&.mobile-only": {
      display: "none",
    },
  },

  "& .back-button, & .next-button": {
    padding: "0",
    width: "52px",
    height: "52px",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transformOrigin: "center",
  },
  "& .next-button": {
    backgroundColor: "white",
    filter: "invert(1)",
    [`.${darkTheme.className} &`]: {
      filter: "none",
    },
    opacity: 0,
    border: "none",
    marginTop: "12px",
    cursor: "pointer",
    transition: "all 0.3s ease-out",
    position: "absolute",
    top: "34px",
    right: "-60px",
    zIndex: 100,
    transform: "rotate(180deg)",

    margin: "0 auto",
    "&.visible-true": {
      opacity: 1,
    },
    "&:hover": {
      opacity: 0.8,
    },
    "@mobile": {
      position: "fixed",
      top: "62px",
      right: "16px",
      left: "unset",
      margin: 0,
    },
  },
  "& .back-button": {
    backgroundColor: "transparent",

    border: "none",
    position: "absolute",
    top: "-140px",
    left: "-60px",
    zIndex: 100,
    margin: "0 auto",

    cursor: "pointer",
    "&:hover": {
      opacity: 0.8,
    },
    "@mobile": {
      top: "62px",
      left: "4px",
      right: "unset",
      position: "fixed",
    },
    "& img": {
      [`.${darkTheme.className} &`]: {
        filter: "invert(1)",
      },
    },
  },

  "& .skip-button": {
    fontSize: "12px",
    textDecoration: "underline",
    cursor: "pointer",
    marginTop: "30px",
    "@mobile": {
      marginTop: "24px",
      transition: "margin-top 0.3s ease-out",
      "&.has-arrow-false": {
        marginTop: "-24px",
      },
    },
    "&:hover": {
      opacity: 0.8,
    },
  },

  "& .initial-content": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "5px",
  },

  "& .taglines": {
    display: "flex",
    zIndex: "10",
    marginTop: "2px",
    "& .tagline": {
      marginLeft: "12px",
      marginRight: "12px",
      display: "flex",
      fontSize: "12px",
      "@mobile": {
        fontSize: "13px",
      },
      alignSelf: "center",
      justifyContent: "center",
      alignItems: "center",
      "& img": {
        marginRight: "4px",
        [`.${darkTheme.className} &`]: {
          filter: "invert(1)",
        },
      },
      [`.${darkTheme.className} &`]: {
        color: "$text",
      },
    },
    "@mobile": {
      flexDirection: "column !important",
      zIndex: "0",
    },
  },

  "& .topic-divider": {
    fontFamily: "KeplerLightCondensed",
    fontSize: "24px",
    marginTop: "40px",
    marginBottom: "14px",
    "@mobile": {
      marginTop: "-28px",
      transition: "margin-top 0.3s ease-out",
      "&.has-arrow-true": {
        marginTop: "24px",
      },
    },
  },

  "& .input-row": {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    "@mobile": {
      width: "100%",
    },
  },

  "& .forward-arrow-container": {
    opacity: 0,
    transition: "opacity 0.3s ease-out",
    position: "absolute",
    left: "100%",
    top: "0",
    marginLeft: "12px",
    "@mobile": {
      position: "static",
      marginLeft: "0",
      marginTop: "10px",
    },
    "&.visible-true": {
      opacity: 1,
    },
    "& .forward-arrow-button": {
      width: "48px",
      height: "48px",
      borderRadius: "50%",
      backgroundColor: "white",
      filter: "invert(1)",
      [`.${darkTheme.className} &`]: {
        filter: "none",
      },
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      cursor: "pointer",
      transition: "all 0.2s ease-out",
      "&:hover": {
        transform: "scale(1.1)",
      },
    },
  },

  "& .content": {
    transition: "all 0.6s ease-out",
    width: "550px",
    opacity: 1,
    position: "relative",
    zIndex: 10,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    [`.${darkTheme.className} &`]: {
      color: "$text",
    },
    "@mobile": {
      width: "100vw !important",
    },
  },

  "& .theme-select": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    position: "relative",
    "@mobile": {
      width: "100%",
      padding: "0 30px 200px 30px",
      boxSizing: "border-box",
    },
    "& .input-container": {
      position: "relative",
      width: "430px",
      "@mobile": {
        width: "100%",
      },
    },
    "& .character-counter": {
      position: "absolute",
      bottom: "-8px",
      right: "10px",
      fontSize: "10px",
      color: "rgba(0, 0, 0, 0.5)",
      [`.${darkTheme.className} &`]: {
        color: "$textMuted",
      },
    },
    "& input": {
      color: "black",
      borderRadius: "24px",
      width: "100%",
      height: "48px !important",
      lineHeight: "1.5",
      fontSize: "13px",
      padding: "14px 20px",
      marginBottom: "12px",
      resize: "none",
      overflowX: "auto",
      overflowY: "hidden",
      whiteSpace: "nowrap",
      boxSizing: "border-box",
      border: "1px solid rgba(0, 0, 0, 0.3)",
      backgroundColor: "rgba(255, 255, 255, 0.5)",
      [`.${darkTheme.className} &`]: {
        color: "$text",
        border: "1px solid $inputBorder",
        backgroundColor: "$inputBg",
      },
      "&::placeholder": {
        color: "rgba(0,0,0,0.7)",
        fontStyle: "italic",
        [`.${darkTheme.className} &`]: {
          color: "$textMuted",
        },
      },
      "&:focus, &.selected-true": {
        outline: "none",
        border: "1px solid black",
        backgroundColor: "white",
        [`.${darkTheme.className} &`]: {
          border: "1px solid $border",
          backgroundColor: "$surface",
        },
      },
      "&:not(:focus):hover": {
        backgroundColor: "rgba(255, 255, 255, 0.6)",
        [`.${darkTheme.className} &`]: {
          backgroundColor: "$surface",
        },
      },
    },
    "& .theme-button-container": {
      display: "flex",
      flexWrap: "wrap",
      width: "440px",
      marginTop: "10px",
      justifyContent: "space-around",
      "@mobile": {
        width: "340px !important",
      },

      "& button": {
        width: "130px",
        "@mobile": {
          width: "150px !important",
        },
        fontWeight: "400",
        textTransform: "uppercase",
        fontSize: "11px",
        marginBottom: "12px",
        height: "34px",
        borderRadius: "18px",
        padding: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(255, 255, 255, 0.4)",
        boxShadow: "0px 2px 0px black",
        transition: "all 0.2s",
        [`.${darkTheme.className} &`]: {
          color: "$text",
          backgroundColor: "$appBg",
          border: "1px solid rgba(255, 255, 255, 0.4)",
          boxShadow: "0px 2px 0px black",
        },
        "&:hover": {
          boxShadow: "0px 0 0 black",
          transform: "translateY(2px)",
          backgroundColor: "white",
          [`.${darkTheme.className} &`]: {
            boxShadow: "0px 0 0 black",
            backgroundColor: "$surface",
          },
        },
      },
    },
  },

  "& .session-selection-content": {
    "@mobile": {
      width: "90% !important",
      flexDirection: "column",
      alignItems: "center",
      "& .session-option": {
        position: "relative",
        padding: "30px 20px !important",
        marginBottom: "18px",
      },
      "& .price": {
        position: "absolute",
        top: "30px",
        right: "20px",
      },
      "& .price-container": {
        marginTop: "4px !important",
        marginBottom: "-10px !important",
      },
      "& button": {
        marginTop: "16px",
        fontSize: "14px!important"
      },
      "& .session-subtitle": {
        marginTop: "20px !important",
      },
      "& .trial-details": {
        position: "absolute",
        top: "76px",
        right: "20px",
        fontSize: "12px!important"
      },
      "& .trial-price": {
        position: "absolute",
        top: "84px",
        right: "20px",
        fontSize: "12px!important"
      },
    },
  },

  "& .session-option": {
    "@mobile": {
      width: "100% !important",
    },
  },

  "& .crossroads-button-container": {
    display: "flex", // This enables flexbox
    flexWrap: "wrap", // Allows buttons to wrap if the container is too narrow
    justifyContent: "space-around", // Adds space between buttons
    width: "500px", // Set an explicit width for the container
    margin: "0 auto", // Center the container horizontally
    marginTop: "30px",
    "& button": {
      margin: "10px", // Adds margin around the buttons
      padding: "20px 0px", // Space inside the button
      backgroundColor: "rgba(255, 255, 255, 0.1)", // Transparent white
      color: "black",
      border: "1px solid rgba(255, 255, 255, 0.5)", // Transparent black
      borderRadius: "18px",
      cursor: "pointer",
      width: "130px", // Ensure buttons don’t get too wide
      flexShrink: 0, // Prevent buttons from shrinking
      transition: "all 0.3s ease-out",
      "&:hover": {
        backgroundColor: "rgba(255, 255, 255, 0.4)",
        width: "160px",
      },
    },
  },

  "& .question-select": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    position: "relative",
    "@mobile": {
      width: "100%",
      padding: "0 30px 200px 30px",
      boxSizing: "border-box",
    },
    "& .title.mobile-only": {
      display: "none",
      "@mobile": {
        display: "block",
      },
    },
    "& .question-button-container": {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    "& .character-counter": {
      position: "absolute",
      top: "54px",
      right: "38px",
      fontSize: "10px",
      color: "rgba(0, 0, 0, 0.5)",
      [`.${darkTheme.className} &`]: {
        color: "$textMuted",
      },
    },
    "& input": {
      color: "black",
      borderRadius: "24px",
      width: "500px !important",
      "@mobile": {
        width: "100% !important",
      },
      height: "48px !important",
      lineHeight: "1.5",
      fontSize: "13px",
      padding: "14px 58px 14px 20px",
      marginBottom: "12px",
      resize: "none",
      overflowX: "auto",
      overflowY: " hidden",
      whiteSpace: "nowrap",
      boxSize: "border-box",
      border: "1px solid rgba(0, 0, 0, 0.3)",
      backgroundColor: "rgba(255, 255, 255, 0.3)",
      [`.${darkTheme.className} &`]: {
        color: "$text",
        border: "1px solid $inputBorder",
        backgroundColor: "$inputBg",
      },

      "&::placeholder": {
        color: "rgba(0,0,0,0.7)",
        fontStyle: "italic",
        [`.${darkTheme.className} &`]: {
          color: "$textMuted",
        },
      },
      "&:focus, &.selected-true": {
        outline: "none",
        border: "1px solid black",
        backgroundColor: "white",
        [`.${darkTheme.className} &`]: {
          border: "1px solid $border",
          backgroundColor: "$surface",
        },
      },
      "&:not(:focus):hover": {
        backgroundColor: "rgba(255, 255, 255, 0.6)",
        [`.${darkTheme.className} &`]: {
          backgroundColor: "$surface",
        },
      },
    },
    "& .question-option-container": {
      width: "430px",
      marginTop: "0px",
      fontSize: "13px",
      color: "rgba(0, 0, 0, 0.7)",
      borderRadius: "18px",
      [`.${darkTheme.className} &`]: {
        color: "$textMuted",
      },
      "@mobile": {
        width: "100% !important",

        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center",
      },
      "& .question-option": {
        padding: "14px 20px",
        backgroundColor: "rgba(255, 255, 255, 0.4)",
        marginBottom: "10px",
        borderRadius: "24px",
        border: "1px solid rgba(0, 0, 0, 0.3)",
        fontSize: "12px",
        cursor: "pointer",
        transition: "all 0.2s",
        [`.${darkTheme.className} &`]: {
          backgroundColor: "rgba(15, 15, 15, 0.6)",
          border: "1px solid rgba(255, 255, 255, 0.15)",
        },
        "@mobile": {
          width: "100%",
        },
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          [`.${darkTheme.className} &`]: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
          },
        },
        "&.active-true": {
          backgroundColor: "white",
          border: "1px solid black",
          color: "black",
          [`.${darkTheme.className} &`]: {
            backgroundColor: "$surface",
            border: "1px solid $border",
            color: "$text",
          },
        },
      },
      "&.visible-true": {
        opacity: 1,
      },
      "&.visible-false": {
        opacity: 1,
      },
      transition: "all 0.6s ease-out",
    },
  },
  variants: {
    isConnectingToRoom: {
      true: {
        "& .back-button, & .next-button": {
          opacity: 0,
          pointerEvents: "none",
        },
      },
    },
    isTransitioning: {
      true: {
        "& .content": {
          opacity: 0,
          transition: "all 0.6s ease-out",
        },
        "& .text-container": {
          opacity: 0,
          transition: "all 0.6s ease-out",
        },
      },
      false: {
        "& .content": {
          opacity: 1,
          transition: "all 0.6s ease-in",
        },
        "& .text-container": {
          opacity: 1,
          transition: "all 0.6s ease-in",
        },
      },
    },
  },
});
